import React, { useEffect, useState, useCallback } from "react";
import { fetchInviteLinksByUserId } from "./PoolModalHelper";
import { supabase } from "../ReusableComponents/supabaseClient";
import PoolDetailsModal from "./PoolDetailsModal";
import StatusBadge from "../ReusableComponents/StatusBadge";
import moment from "moment";
import "moment-timezone";
import { useNavigate } from "react-router-dom";
import { IoChevronBackOutline } from "react-icons/io5";
import { FaPlus } from "react-icons/fa";
import { logError } from "../Utilities/errorLogger";
import { useAuth } from "../Utilities/AuthContext";

const AllPools = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const navigate = useNavigate();
  const { ownerData } = useAuth();
  const userId = ownerData?.userId || localStorage.getItem("userId");

  const refreshData = useCallback(async () => {
    if (!userId) return;
    try {
      const fetchedData = await fetchInviteLinksByUserId(userId);
      setData(fetchedData);
    } catch (error) {
      await logError({
        filePath: "Pool/AllPools.js",
        functionName: "refreshData",
        errorType: "Pools Fetch Error",
        errorMessage: error.message,
        stackTrace: error.stack,
        requestData: { userId },
      });
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    if (userId) {
      refreshData();

      const channel = supabase
        .channel("allPoolsChanges")
        .on("postgres_changes", { event: "*", schema: "public", table: "InviteLink", filter: `person=eq.${userId}` }, () => refreshData())
        .on("postgres_changes", { event: "*", schema: "public", table: "pool" }, () => refreshData())
        .subscribe();

      return () => { supabase.removeChannel(channel); };
    }
  }, [userId, refreshData]);

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-6 md:pt-20">
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="animate-pulse bg-white rounded-xl p-4 flex justify-between items-center">
              <div className="space-y-2 flex-1">
                <div className="h-4 bg-gray-200 rounded w-2/3" />
                <div className="h-3 bg-gray-100 rounded w-1/3" />
              </div>
              <div className="h-6 w-20 bg-gray-200 rounded-full" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-6 md:pt-20 mb-24 md:mb-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-primary to-primary-light rounded-2xl p-6 mb-6 shadow-lg">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate(-1)} className="text-green-200 hover:text-white transition-colors">
              <IoChevronBackOutline className="w-5 h-5" />
            </button>
            <h1 className="text-2xl font-bold text-white tracking-tight">My Pools</h1>
          </div>
          <button
            onClick={() => setModalIsOpen(true)}
            className="flex items-center gap-2 bg-white text-primary font-bold py-2.5 px-5 rounded-xl hover:bg-green-50 transition-all text-sm shadow-sm"
          >
            <FaPlus className="w-3 h-3" /> New Pool
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
      {data.length === 0 ? (
        <div className="p-12 text-center">
          <p className="text-gray-400 text-lg">No pools yet. Create one to get started!</p>
        </div>
      ) : (
        <div className="divide-y divide-gray-50">
          {data.map((item) => (
            <div
              key={item.id}
              onClick={() => navigate(`/pool/${item.pool?.id}`)}
              className="flex items-center justify-between px-5 py-4 hover:bg-gray-50 transition-all cursor-pointer"
            >
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold text-gray-900 truncate">
                  {item.pool?.name || item.pool?.tournament_fixture?.name || `Pool #${item.pool?.id}`}
                </p>
                <p className="text-xs text-gray-500 mt-0.5">
                  {item.pool?.tournament_fixture?.start_date
                    ? moment(item.pool.tournament_fixture.start_date).format("MMM D, YYYY")
                    : "No date"}
                </p>
              </div>
              <StatusBadge status={item.pool?.tournament_status || "Not Started"} />
            </div>
          ))}
        </div>
      )}
      </div>

      {modalIsOpen && (
        <PoolDetailsModal isOpen={modalIsOpen} onClose={() => setModalIsOpen(false)} mode="insert" loggedInUser={Number(userId)} />
      )}
    </div>
  );
};

export default AllPools;
